import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack} from 'views/components';
import {useNavigate, isInactiveEvent} from 'views/module';

import ClientTile from './sections/ClientTile';
import CaregiverTile from './sections/CaregiverTile';
import EventHeader from './sections/EventHeader';
import TimeTile from './sections/TimeTile';
import NotesTile from './sections/NotesTile';
import ReimbursementTile from './sections/ReimbursementTile';
import ShiftFeedbackTile from './sections/ShiftFeedbackTile';
import EventTimelineTile from './sections/EventTimelineTile';
import LoadingScreen from './skeleton/LoadingScreen';
import Popup from './sections/Popup';
import {isResolvableActorRn, formatChangedByLabel} from './changedBy';
import SCHEDULE_SOURCES from '../schedule_source';

import backend from 'utils/backend';

import moment from 'moment-timezone';

const EventDetail = (props) => {
  const {eventRn, statusProgressWrap: progressWrap, user} = props;
  const scheduleSource = props.scheduleSource || SCHEDULE_SOURCES.LEGACY;

  const [event, setEvent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [model, setModel] = React.useState(null);
  const [reimbursement, setReimbursement] = React.useState({});
  const [shiftFeedback, setShiftFeedback] = React.useState(null);
  const [changedByLabel, setChangedByLabel] = React.useState(null);

  const navigate = useNavigate();

  const handleCancelEvent = async (clientRn, eventRn, reasonForException, onSuccess=() => undefined) => {
    progressWrap(async () => {
      await backend.event.cancelEvent(clientRn, eventRn, reasonForException);
      navigate(-1);
      onSuccess();
    });
  };

  const handleRescheduleEvent = async (clientRn, eventRn, reasonForException, eventData, shiftType, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const output = await backend.event.rescheduleEvent(clientRn, eventRn, reasonForException, eventData, shiftType);

      navigate(`/schedule/event/${output.eventRn}`, {});

      onSuccess();
    });
  };

  const handleDeclineRequest = async (eventRn, type, declineReason, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const adminAction = {
        status: 'DECLINED',
        reason: declineReason,
      };

      const {eventUpdateData} = await backend.event.declineEvent(eventRn, type, adminAction);

      setEvent((e) => {
        const caregiverUpdates = e.caregiverUpdates;

        const caregiverUpdateIndex = caregiverUpdates.findIndex((e) => e.type === type);

        caregiverUpdates[caregiverUpdateIndex] = eventUpdateData;

        return {
          ...e,
          caregiverUpdates: [...caregiverUpdates],
        };
      });


      onSuccess();
    });
  };

  const handleUpdateEventInfo = async (clientRn, eventRn, info, shiftType, onSuccess=() => undefined) => {
    progressWrap(async () => {
      await backend.event.updateEventInfo(clientRn, eventRn, info, shiftType);

      setEvent((e) => {
        return {
          ...e,
          info,
        };
      });

      onSuccess();
    });
  };

  const handleUpdateEditedClockOut = async (eventRn, type, clockOutTime, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const prev = event.caregiverUpdates.find((e) => e.type === type);
      const newData = {
        ...prev.data,
        suggestedClockoutTimeFormatted: clockOutTime,
      };
      await backend.event.createEventUpdate(eventRn, type, newData);

      setEvent((e) => {
        const caregiverUpdates = e.caregiverUpdates;
        const caregiverUpdateIndex = caregiverUpdates.findIndex((e) => e.type === type);
        const prev = caregiverUpdates[caregiverUpdateIndex];

        caregiverUpdates[caregiverUpdateIndex] = {
          ...prev,
          data: {
            ...prev.data,
            suggestedClockoutTimeFormatted: clockOutTime,
          },
        };
        return {
          ...e,
          caregiverUpdates: [...caregiverUpdates],
        };
      });
      onSuccess();
    });
  };

  const handleUpdateEditedClockIn = async (eventRn, type, clockInTime, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const prev = event.caregiverUpdates.find((e) => e.type === type);
      const newData = {
        ...prev.data,
        suggestedClockinTimeFormatted: clockInTime,
      };
      await backend.event.createEventUpdate(eventRn, type, newData);

      setEvent((e) => {
        const caregiverUpdates = e.caregiverUpdates;
        const caregiverUpdateIndex = caregiverUpdates.findIndex((e) => e.type === type);
        const prev = caregiverUpdates[caregiverUpdateIndex];

        caregiverUpdates[caregiverUpdateIndex] = {
          ...prev,
          data: {
            ...prev.data,
            suggestedClockinTimeFormatted: clockInTime,
          },
        };
        return {
          ...e,
          caregiverUpdates: [...caregiverUpdates],
        };
      });
      onSuccess();
    });
  };

  const handleAdminClockOut = async (eventRn, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const type = 'clockOutConfirmation';
      const now = moment().toISOString();
      const newData = {
        suggestedClockoutTimeFormatted: now,
        actualClockoutTimeFormatted: now,
        adminInitiated: true,
        adminRn: user.userRn,
        adminFirstName: user.firstName,
        adminLastName: user.lastName,
      };
      await backend.event.createEventUpdate(eventRn, type, newData);

      setEvent((e) => {
        const caregiverUpdates = e.caregiverUpdates.filter((u) => u.type !== type);

        return {
          ...e,
          caregiverUpdates: [...caregiverUpdates, {type, data: newData, createdAt: now}],
        };
      });

      onSuccess();
    });
  };

  const handleAdminClockIn = async (eventRn, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const type = 'clockInConfirmation';
      const now = moment().toISOString();
      const newData = {
        suggestedClockinTimeFormatted: now,
        actualClockinTimeFormatted: now,
        adminInitiated: true,
        adminRn: user.userRn,
        adminFirstName: user.firstName,
        adminLastName: user.lastName,
      };
      await backend.event.createEventUpdate(eventRn, type, newData);

      setEvent((e) => {
        const caregiverUpdates = e.caregiverUpdates.filter((u) => u.type !== type);

        return {
          ...e,
          caregiverUpdates: [...caregiverUpdates, {type, data: newData, createdAt: now}],
        };
      });

      onSuccess();
    });
  };

  const handleUpdateAllEventInfo = async (clientRn, recurrenceRn, info, shiftType, onSuccess=() => undefined) => {
    progressWrap(async () => {
      await backend.event.updateAllEventInfo(clientRn, recurrenceRn, info, shiftType);

      setEvent((e) => {
        return {
          ...e,
          info,
        };
      });

      onSuccess();
    });
  };

  const handleUpdateShiftType = async (clientRn, eventRn, shiftType, onSuccess=() => undefined) => {
    progressWrap(async () => {
      await backend.event.updateShiftType(clientRn, eventRn, shiftType);

      setEvent((e) => {
        return {
          ...e,
          shiftType,
        };
      });

      onSuccess();
    });
  };

  const handleUpdateEventCaregiver = async (clientRn, eventRn, caregiverData, reasonForException, onSuccess=() => undefined) => {
    progressWrap(async () => {
      const output = await backend.event.updateEventCaregiver(clientRn, eventRn, caregiverData, reasonForException);

      navigate(`/schedule/event/${output.eventRn}`, {});

      onSuccess();
    });
  };
  React.useEffect(() => {
    progressWrap(async () => {
      setLoading(true);
      const result = await scheduleSource.loadEvent(eventRn);
      setEvent(result.event);
      setLoading(false);
    }, {showProgress: false});
  }, [eventRn]);

  React.useEffect(() => {
    const feedbackRn = new URLSearchParams(window.location.search).get('feedbackRn');
    if (!feedbackRn) {
      setShiftFeedback(null);
      return;
    }

    progressWrap(async () => {
      const feedback = await backend.clientSupport.getEventFeedback(feedbackRn);
      setShiftFeedback(feedback);
    }, {showProgress: false});
  }, [eventRn]);

  React.useEffect(() => {
    progressWrap(async () => {
      const reimbursement = await backend.expenseReimbursement.getExpenseReimbursement(eventRn);
      setReimbursement(reimbursement);
    }, {showProgress: false});
  }, [eventRn]);

  // Resolved once per event, never per calendar tile. Admins, caregivers and client contacts are all Identity-FE
  // users; internal service RNs are not resolvable and stay displayed as the raw RN by the timeline.
  const changedByRn = event?.deactivatedData?.changedByRn;
  React.useEffect(() => {
    setChangedByLabel(null);

    if (!isResolvableActorRn(changedByRn)) {
      return;
    }

    progressWrap(async () => {
      try {
        const result = await backend.user.getUser(changedByRn, false);
        setChangedByLabel(formatChangedByLabel(result?.user));
      } catch (error) {
        console.error('Failed to load the user who deactivated this event');
      }
    }, {showProgress: false});
  }, [changedByRn]);

  if (loading) {
    return <LoadingScreen />;
  }

  const isInactive = isInactiveEvent(event);

  return (
    <Box sx={{p: 3, margin: 'auto'}}>
      <Popup
        model={model}
        setModel={setModel}
        event={event}
        handleUpdateEventCaregiver={handleUpdateEventCaregiver}
        handleCancelEvent={handleCancelEvent}
        handleRescheduleEvent={handleRescheduleEvent}
        handleDeclineRequest={handleDeclineRequest}
        handleUpdateEditedClockOut={handleUpdateEditedClockOut}
        handleUpdateEditedClockIn={handleUpdateEditedClockIn}
        handleAdminClockOut={handleAdminClockOut}
        handleAdminClockIn={handleAdminClockIn}
      />
      <EventHeader
        event={event}
        setModel={setModel}
        onUpdateShiftType={handleUpdateShiftType}
      />
      <Stack
        direction={{xs: 'column', sm: 'row'}}
        spacing={2}
        mb={2}
      >
        <Box sx={{width: '100%'}}>
          <ClientTile client={event.clientData} />
        </Box>
        <Box sx={{width: '100%'}}>
          <CaregiverTile
            caregiver={event.caregiverData}
            setModel={setModel}
            disableChanges={isInactive}
          />
        </Box>
      </Stack>
      <TimeTile event={event} />
      {
        shiftFeedback &&
        <ShiftFeedbackTile feedbackData={shiftFeedback} />
      }
      <NotesTile event={event}
        onUpdateEventInfo={handleUpdateEventInfo}
        onUpdateAllEventInfo={handleUpdateAllEventInfo}
      />
      {
        reimbursement.expenseReimbursement &&
        <Box sx={{width: '100%'}}>
          <ReimbursementTile reimbursement={reimbursement} />
        </Box>
      }
      <EventTimelineTile
        event={event}
        setModel={setModel}
        changedByLabel={changedByLabel}
        handleRescheduleEvent={handleRescheduleEvent}
        handleCancelEvent={handleCancelEvent}
      />
    </Box>
  );
};

EventDetail.propTypes = {
  eventRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  user: PropTypes.object,
  scheduleSource: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(EventDetail));
